import { SaleReceiptPanel } from './SaleReceiptPanel';
import { GiftPackagePanel } from './GiftPackagePanel';
import { useFinancialCapabilities } from './financialShared';
import { Fragment, useRef, useState } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberPackageDetailDto, PackageTypeDto } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { safeFormatDate } from '../../../lib/format';
import { errorMessage } from './errorMessage';
import { MonthlyPlanPanel } from './MonthlyPlanPanel';
import {
  BUTTON,
  Field,
  FormError,
  INPUT,
  PANEL,
  SMALL_BUTTON,
  TournamentSelect,
  formatDay,
  isUncertainFailure,
  reversalKey,
  useOperationKey,
  useWalletPermissions,
} from './walletShared';

type Action = { kind: 'use' | 'extend' | 'void'; pkg: MemberPackageDetailDto };

/** Reload the passes (and ZIPA) so staff see whether an uncertain attempt went through. */
const reloadPasses = (qc: QueryClient, memberId: string) =>
  Promise.all([
    qc.refetchQueries({ queryKey: ['member-packages', memberId] }, { throwOnError: true }),
    qc.refetchQueries({ queryKey: ['member-zipa', memberId] }, { throwOnError: true }),
  ]);

const STATUS_CLASS: Record<MemberPackageDetailDto['status'], string> = {
  USABLE: 'bg-success/15 text-success',
  USED_UP: 'bg-base-300 text-base-content/60',
  EXPIRED: 'bg-warning/15 text-warning',
  VOID: 'bg-base-300 text-base-content/60',
};

/** 堂票紀錄: the 月友 panel, then this venue's passes (S1). */
export function PackagesTab({ memberId, locked }: { memberId: string; locked: boolean }) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const financial = useFinancialCapabilities(memberId);
  let recoveringSale = false;
  let recoveringGift = false;
  try {
    recoveringSale = !!sessionStorage.getItem(`member-financial:${financial.identity}:sales`);
    recoveringGift = !!sessionStorage.getItem(`member-gift:${financial.identity}:GIFT`);
  } catch {
    /* Recovery writes will fail closed. */
  }
  const integratedSales = financial.data?.salePostingAvailable === true || recoveringSale;
  const salesUnresolved =
    (financial.can('member:charge_confirm') || financial.can('member:adjust_points')) &&
    (!financial.venue || !financial.isSuccess);
  const blockLegacySales =
    integratedSales || salesUnresolved || financial.data?.receiptRequired === true;
  const { canAdjust, canManage } = useWalletPermissions();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [error, setError] = useState('');

  const { data: passes, isError } = useQuery<MemberPackageDetailDto[]>({
    queryKey: ['member-packages', memberId, financial.identity, financial.revision],
    queryFn: () => api.get(`/members/${memberId}/packages`),
  });
  const refresh = () => {
    setError('');
    void qc.invalidateQueries({ queryKey: ['member-packages', memberId] });
    void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
    void qc.invalidateQueries({ queryKey: ['member-zipa', memberId] });
    void qc.invalidateQueries({ queryKey: ['member-zipa-balances'] });
  };
  const onError = (failure: unknown) => setError(errorMessage(failure, t('ledgerLoadFailed')));

  // One key per intended reversal (entry and note), kept across a retry after an uncertain failure.
  const reversalKeys = useRef(new Map<string, string>());
  const reverse = useMutation({
    mutationFn: (input: { packageId: string; entryId: string; note: string }) =>
      api.post(
        `/members/${memberId}/packages/${input.packageId}/entries/${input.entryId}/reverse`,
        {
          note: input.note,
          operationKey: reversalKey(reversalKeys.current, input.entryId, input.note),
        },
      ),
    onSuccess: (_data, input) => {
      reversalKeys.current.delete(`${input.entryId}|${input.note}`);
      refresh();
    },
    onError: (failure, input) => {
      // Keep the key only when the reversal may have gone through, so a retry replays it.
      if (isUncertainFailure(failure)) refresh();
      else reversalKeys.current.delete(`${input.entryId}|${input.note}`);
      onError(failure);
    },
  });

  const term = search.trim().toLowerCase();
  const rows = (passes ?? []).filter(
    (p) =>
      !term ||
      p.code.toLowerCase().includes(term) ||
      (p.originalCode ?? '').toLowerCase().includes(term) ||
      p.name.toLowerCase().includes(term),
  );

  return (
    <div className="space-y-4">
      <MonthlyPlanPanel memberId={memberId} locked={locked} integratedSales={blockLegacySales} />
      {(canAdjust || recoveringGift) && !locked && (
        <GiftPackagePanel key={financial.formIdentity} memberId={memberId} cap={financial} />
      )}
      {financial.venue && (integratedSales || financial.can('member:charge_read')) && (
        <SaleReceiptPanel memberId={memberId} cap={financial} />
      )}

      {canAdjust && !locked && !blockLegacySales && (
        <SellPackageForm memberId={memberId} onDone={refresh} />
      )}
      {canManage && !locked && <ReenterPackageForm memberId={memberId} onDone={refresh} />}

      <input
        type="search"
        aria-label={t('walletSearch')}
        placeholder={t('walletSearch')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className={INPUT}
      />

      {action && !locked && (
        <PackageActionForm
          memberId={memberId}
          action={action}
          onCancel={() => setAction(null)}
          onDone={() => {
            setAction(null);
            refresh();
          }}
        />
      )}
      <FormError message={error} />

      <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-base-300">
            <tr>
              {[
                'walletCode',
                'walletName',
                'walletStatus',
                'walletUsesLeft',
                'walletUseCount',
                'walletSource',
                'walletPrice',
                'walletPayment',
                'walletValidUntil',
                'ledgerNote',
                'ledgerTime',
                'walletActions',
              ].map((key) => (
                <th key={key} className="text-left px-3 py-2 whitespace-nowrap">
                  {t(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {rows.map((p) => (
              <Fragment key={p.id}>
                <tr
                  className="cursor-pointer hover:bg-base-300/50"
                  onClick={() => setOpen(open === p.id ? null : p.id)}
                  aria-expanded={open === p.id}
                >
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    {p.code}
                    {p.originalCode && (
                      <span className="block text-xs text-base-content/50">{p.originalCode}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLASS[p.status]}`}>
                      {t(`walletPackageStatus_${p.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono">{p.usesLeft}</td>
                  <td className="px-3 py-2 font-mono">{p.useCount}</td>
                  <td className="px-3 py-2">{t(`walletSource_${p.source}`)}</td>
                  <td className="px-3 py-2 font-mono">
                    {p.gifted
                      ? t('gift.price', { price: p.price })
                      : Number(p.price).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">{t(`walletPayment_${p.payment}`)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {p.validUntil ? formatDay(p.validUntil) : t('walletNoExpiry')}
                  </td>
                  <td className="px-3 py-2 [overflow-wrap:anywhere]">{p.note}</td>
                  <td className="px-3 py-2 text-xs text-base-content/60 whitespace-nowrap">
                    {safeFormatDate(p.createdAt, (d) => d.toLocaleString())}
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    {!locked && p.status !== 'VOID' && (
                      <div className="flex gap-1">
                        {canAdjust && p.status === 'USABLE' && (
                          <button
                            type="button"
                            className={SMALL_BUTTON}
                            onClick={() => setAction({ kind: 'use', pkg: p })}
                          >
                            {t('walletUse')}
                          </button>
                        )}
                        {canManage && (
                          <>
                            <button
                              type="button"
                              className={SMALL_BUTTON}
                              onClick={() => setAction({ kind: 'extend', pkg: p })}
                            >
                              {t('walletExtend')}
                            </button>
                            <button
                              type="button"
                              className={SMALL_BUTTON}
                              onClick={() => setAction({ kind: 'void', pkg: p })}
                            >
                              {t('walletVoid')}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {open === p.id && (
                  <tr>
                    <td colSpan={12} className="px-3 py-2 bg-base-100">
                      <PackageEntries
                        pkg={p}
                        canReverse={canAdjust && !locked && p.status !== 'VOID'}
                        pending={reverse.isPending}
                        onReverse={(entryId, note) =>
                          reverse.mutate({ packageId: p.id, entryId, note })
                        }
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {passes && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-base-content/50">
                  {t('ledgerEmpty')}
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-error">
                  {t('ledgerLoadFailed')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PackageEntries({
  pkg,
  canReverse,
  pending,
  onReverse,
}: {
  pkg: MemberPackageDetailDto;
  canReverse: boolean;
  pending: boolean;
  onReverse: (entryId: string, note: string) => void;
}) {
  const { t } = useTranslation('member');
  const [note, setNote] = useState('');
  return (
    <div className="space-y-2">
      {canReverse && (
        <Field label={t('walletReason')}>
          <input
            className={INPUT}
            maxLength={255}
            value={note}
            disabled={pending}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      )}
      <ul className="text-sm divide-y divide-base-300">
        {pkg.entries.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-2 py-1">
            <span className="font-medium">{t(`walletEntry_${e.source}`)}</span>
            <span className={`font-mono ${e.delta < 0 ? 'text-error' : 'text-success'}`}>
              {e.delta > 0 ? '+' : ''}
              {e.delta}
            </span>
            {e.entryNo !== null && (
              <span className="text-base-content/60">
                {t('walletEntryNoValue', { n: e.entryNo })}
              </span>
            )}
            {e.newValidUntil && (
              <span className="text-base-content/60">
                {formatDay(e.previousValidUntil)} → {formatDay(e.newValidUntil)}
              </span>
            )}
            <span className="text-base-content/60 [overflow-wrap:anywhere]">{e.note}</span>
            <span className="text-xs text-base-content/50">
              {safeFormatDate(e.createdAt, (d) => d.toLocaleString())}
            </span>
            {e.source === 'USE' &&
              (e.reversed ? (
                <span className="text-xs text-base-content/50">{t('walletReversed')}</span>
              ) : (
                canReverse && (
                  <button
                    type="button"
                    className={SMALL_BUTTON}
                    disabled={!note.trim() || pending}
                    onClick={() => onReverse(e.id, note.trim())}
                  >
                    {t('walletReverse')}
                  </button>
                )
              ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

function useTypes() {
  return useQuery<PackageTypeDto[]>({
    queryKey: ['package-types'],
    queryFn: () => api.get('/members/package-types'),
  });
}

function SellPackageForm({ memberId, onDone }: { memberId: string; onDone: () => void }) {
  const { t } = useTranslation('member');
  const { data: types } = useTypes();
  const [typeId, setTypeId] = useState('');
  const [payment, setPayment] = useState<'CASH' | 'ZIPA'>('CASH');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const op = useOperationKey({ typeId, payment, note }, () => reloadPasses(qc, memberId));
  const sell = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/packages`, {
        typeId,
        payment,
        note: note.trim(),
        operationKey: op.key,
      }),
    onSuccess: () => {
      setError('');
      setNote('');
      op.renew();
      onDone();
    },
    onError: (failure) => {
      op.failed(failure);
      setError(errorMessage(failure, t('ledgerLoadFailed')));
    },
  });
  const active = (types ?? []).filter((x) => x.isActive);
  return (
    <form
      className={PANEL}
      onSubmit={(e) => {
        e.preventDefault();
        sell.mutate();
      }}
    >
      <h3 className="font-medium">{t('walletSellPackage')}</h3>
      <fieldset disabled={sell.isPending} className="grid gap-3 sm:grid-cols-3">
        <Field label={t('walletType')}>
          <select className={INPUT} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">{t('walletPickType')}</option>
            {active.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} · {x.useCount} · NT${Number(x.price).toLocaleString()}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('walletPayment')}>
          <select
            className={INPUT}
            value={payment}
            onChange={(e) => setPayment(e.target.value as 'CASH' | 'ZIPA')}
          >
            <option value="CASH">{t('walletPayment_CASH')}</option>
            <option value="ZIPA">{t('walletPayment_ZIPA')}</option>
          </select>
        </Field>
        <Field label={t('ledgerNote')}>
          <input
            className={INPUT}
            maxLength={255}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </fieldset>
      <FormError message={error} />
      <button
        type="submit"
        className={BUTTON}
        disabled={!typeId || !note.trim() || sell.isPending || !op.ready}
      >
        {error ? t('walletRetry') : t('walletSellPackage')}
      </button>
    </form>
  );
}

function ReenterPackageForm({ memberId, onDone }: { memberId: string; onDone: () => void }) {
  const { t } = useTranslation('member');
  const { data: types } = useTypes();
  const [show, setShow] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [usesLeft, setUsesLeft] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const op = useOperationKey({ typeId, usesLeft, validUntil, originalCode, note }, () =>
    reloadPasses(qc, memberId),
  );
  const save = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/packages/reentry`, {
        typeId,
        usesLeft: Number(usesLeft),
        validUntil: validUntil || null,
        originalCode: originalCode.trim(),
        note: note.trim(),
        operationKey: op.key,
      }),
    onSuccess: () => {
      setError('');
      setUsesLeft('');
      setOriginalCode('');
      setNote('');
      op.renew();
      onDone();
    },
    onError: (failure) => {
      op.failed(failure);
      setError(errorMessage(failure, t('ledgerLoadFailed')));
    },
  });
  if (!show)
    return (
      <button type="button" className={SMALL_BUTTON} onClick={() => setShow(true)}>
        {t('walletReenter')}
      </button>
    );
  return (
    <form
      className={PANEL}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3 className="font-medium">{t('walletReenterPackage')}</h3>
      <fieldset disabled={save.isPending} className="grid gap-3 sm:grid-cols-3">
        <Field label={t('walletType')}>
          <select className={INPUT} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">{t('walletPickType')}</option>
            {(types ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('walletUsesLeft')}>
          <input
            className={INPUT}
            type="number"
            min="1"
            inputMode="numeric"
            value={usesLeft}
            onChange={(e) => setUsesLeft(e.target.value)}
          />
        </Field>
        <Field label={t('walletValidUntil')}>
          <input
            className={INPUT}
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </Field>
        <Field label={t('walletOriginalCode')}>
          <input
            className={INPUT}
            maxLength={40}
            value={originalCode}
            onChange={(e) => setOriginalCode(e.target.value)}
          />
        </Field>
        <Field label={t('ledgerNote')}>
          <input
            className={INPUT}
            maxLength={255}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </fieldset>
      <FormError message={error} />
      <div className="flex gap-2">
        <button
          type="submit"
          className={BUTTON}
          disabled={
            !typeId ||
            !(Number(usesLeft) >= 1) ||
            !originalCode.trim() ||
            !note.trim() ||
            save.isPending ||
            !op.ready
          }
        >
          {error ? t('walletRetry') : t('ledgerSave')}
        </button>
        <button type="button" className={SMALL_BUTTON} onClick={() => setShow(false)}>
          {t('walletCancel')}
        </button>
      </div>
    </form>
  );
}

function PackageActionForm({
  memberId,
  action,
  onCancel,
  onDone,
}: {
  memberId: string;
  action: Action;
  onCancel: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('member');
  const [tournamentId, setTournamentId] = useState('');
  const [entryNo, setEntryNo] = useState('1');
  const [validUntil, setValidUntil] = useState(action.pkg.validUntil ?? '');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const op = useOperationKey(
    {
      kind: action.kind,
      id: action.pkg.id,
      tournamentId,
      entryNo,
      validUntil,
      text,
    },
    () => reloadPasses(qc, memberId),
  );
  const base = `/members/${memberId}/packages/${action.pkg.id}`;
  const submit = useMutation({
    mutationFn: () => {
      if (action.kind === 'use')
        return api.post(`${base}/use`, {
          tournamentId,
          entryNo: Number(entryNo),
          note: text.trim(),
          operationKey: op.key,
        });
      if (action.kind === 'extend')
        return api.post(`${base}/extend`, {
          validUntil,
          reason: text.trim(),
          operationKey: op.key,
        });
      return api.post(`${base}/void`, { reason: text.trim() });
    },
    onSuccess: () => {
      op.renew();
      onDone();
    },
    onError: (failure) => {
      op.failed(failure);
      setError(errorMessage(failure, t('ledgerLoadFailed')));
    },
  });
  const valid =
    !!text.trim() &&
    (action.kind !== 'use' || (!!tournamentId && Number(entryNo) >= 1)) &&
    (action.kind !== 'extend' || !!validUntil);
  const title = {
    use: t('walletUse'),
    extend: t('walletExtend'),
    void: t('walletVoid'),
  }[action.kind];
  return (
    <form
      className={PANEL}
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <h3 className="font-medium">
        {title} · <span className="font-mono">{action.pkg.code}</span>
      </h3>
      <fieldset disabled={submit.isPending} className="grid gap-3 sm:grid-cols-3">
        {action.kind === 'use' && (
          <>
            <Field label={t('walletTournament')}>
              <TournamentSelect value={tournamentId} onChange={setTournamentId} />
            </Field>
            <Field label={t('walletEntryNo')}>
              <input
                className={INPUT}
                type="number"
                min="1"
                inputMode="numeric"
                value={entryNo}
                onChange={(e) => setEntryNo(e.target.value)}
              />
            </Field>
          </>
        )}
        {action.kind === 'extend' && (
          <Field label={t('walletValidUntil')}>
            <input
              className={INPUT}
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
          </Field>
        )}
        <Field label={action.kind === 'use' ? t('ledgerNote') : t('walletReason')}>
          <input
            className={INPUT}
            maxLength={255}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
      </fieldset>
      {action.kind === 'use' && (
        <p className="text-xs text-base-content/60">{t('walletEntryNoHint')}</p>
      )}
      <FormError message={error} />
      <div className="flex gap-2">
        <button type="submit" className={BUTTON} disabled={!valid || submit.isPending || !op.ready}>
          {error ? t('walletRetry') : title}
        </button>
        <button type="button" className={SMALL_BUTTON} onClick={onCancel}>
          {t('walletCancel')}
        </button>
      </div>
    </form>
  );
}
